/**
 * Barras: eje X = estado actual, color = campaña u origen.
 * @module-isolated analytics-cohort
 */
import { useMemo } from 'react';
import {
  STATE_UNCLASSIFIED,
  kommoStageKey,
  uniqueByLeadId
} from '../utils/filter-cohort.js';

const SERIES_COLORS = [
  '#1d4ed8',
  '#0f766e',
  '#c2410c',
  '#7c3aed',
  '#b45309',
  '#be123c',
  '#0369a1',
  '#4d7c0f',
  '#a16207',
  '#6d28d9',
  '#0e7490',
  '#9f1239'
];

function fmt(n) {
  return Number(n || 0).toLocaleString('es-CL');
}

function pct(part, total) {
  const p = Number(part) || 0;
  const t = Number(total) || 0;
  if (!t || !p) return '0%';
  return (Math.round((p / t) * 1000) / 10).toLocaleString('es-CL') + '%';
}

const ARRIVAL_RULES = [
  { re: /gicapital\s*diagn[oó]stico/i, label: 'GICapital Diagnostico' },
  { re: /contacto\s*web/i, label: 'Contacto web GICapital' },
  { re: /landing\s*gicapital/i, label: 'Landing GICapital' }
];

function leadTags(lead) {
  return [lead && lead.originTag]
    .concat(String((lead && lead.tagsText) || '').split(/[;|,]/))
    .map((part) => String(part || '').trim())
    .filter(Boolean);
}

function metaSide(lead, catalog) {
  const id = String((lead && lead.metaCampaignId) || '').trim();
  if (id && catalog.has(id)) return catalog.get(id);
  const name = String((lead && lead.metaCampaignName) || '').trim();
  if (name && !/diagn[oó]stico|contacto web|landing gicapital/i.test(name)) return name;
  return 'Sin campaña Meta en el lead';
}

function arrivalOf(lead, catalog) {
  const tags = leadTags(lead);
  const rule = ARRIVAL_RULES.find((item) => tags.some((tag) => item.re.test(tag)));
  const meta = metaSide(lead, catalog);
  if (rule) {
    return {
      key: 'a|' + rule.label + '|' + meta,
      origin: rule.label,
      meta: meta,
      label: rule.label + ' · ' + meta
    };
  }
  const campTag = tags.find((tag) => /campa[nñ¤]|invierte/i.test(tag));
  if (campTag) {
    const origin = campTag.replace(/Ã±/g, 'ñ').replace(/¤/g, 'ñ');
    return {
      key: 't|' + origin.toLowerCase() + '|' + meta,
      origin: origin,
      meta: meta,
      label: origin + (meta === origin ? '' : ' · ' + meta)
    };
  }
  if (lead.originStatus === 'none') {
    return { key: 'o|none|' + meta, origin: 'Sin etiqueta', meta: meta, label: 'Sin etiqueta · ' + meta };
  }
  const origin = String((lead && lead.originTag) || 'Sin clasificar');
  return { key: 'o|' + origin + '|' + meta, origin: origin, meta: meta, label: origin + ' · ' + meta };
}

function stateOf(lead, mode) {
  if (mode === 'kommo') {
    return { id: kommoStageKey(lead), name: '' };
  }
  if (lead.unclassified || !lead.internalStateId) {
    return { id: STATE_UNCLASSIFIED, name: 'Sin clasificar' };
  }
  return {
    id: String(lead.internalStateId),
    name: String(lead.internalStateName || lead.internalStateId)
  };
}

export default function StatusByCampaignChart({ leads, namedCampaigns, stateRows, mode }) {
  const model = useMemo(() => {
    const catalog = new Map(
      (namedCampaigns || [])
        .filter((row) => row && row.id && row.name)
        .map((row) => [String(row.id), String(row.name)])
    );
    const order = (stateRows || []).map((r) => ({
      id: r.id,
      name: r.name,
      counts: {}
    }));
    const byId = new Map(order.map((s) => [s.id, s]));
    const seriesMap = new Map();

    uniqueByLeadId(leads).forEach((lead) => {
      const st = stateOf(lead, mode);
      let bucket = byId.get(st.id);
      if (!bucket) {
        bucket = { id: st.id, name: st.name || st.id, counts: {} };
        byId.set(st.id, bucket);
        order.push(bucket);
      } else if (!bucket.name && st.name) {
        bucket.name = st.name;
      }
      const serie = arrivalOf(lead, catalog);
      const prev = seriesMap.get(serie.key);
      if (!prev) seriesMap.set(serie.key, { ...serie, total: 1 });
      else prev.total += 1;
      bucket.counts[serie.key] = (bucket.counts[serie.key] || 0) + 1;
    });

    const counted = Array.from(seriesMap.values()).filter((s) => s.total > 0);
    const series = (counted.length ? counted : Array.from(seriesMap.values()))
      .sort((a, b) => b.total - a.total)
      .map((s, i) => ({
        ...s,
        color: s.key === 'o|none' || s.key === 'o|unclassified' ? '#94a3b8' : SERIES_COLORS[i % SERIES_COLORS.length]
      }));

    let max = 1;
    order.forEach((st) => {
      series.forEach((s) => {
        const n = st.counts[s.key] || 0;
        if (n > max) max = n;
      });
    });

    return { states: order.filter((s) => series.some((ser) => s.counts[ser.key])), series, max };
  }, [leads, namedCampaigns, stateRows, mode]);

  if (!model.series.length) {
    return <p className="analytics-empty">Sin leads en los orígenes marcados.</p>;
  }

  const grand = model.series.reduce((n, s) => n + (s.total || 0), 0);

  return (
    <section className="analytics-cohort-states" aria-label="Leads por estado y campaña">
      <div className="analytics-cohort-states-head">
        <h2 className="analytics-cohort-h2">Cómo llegaron</h2>
        <p className="analytics-cohort-note">
          Cada color es la etiqueta con la que entraron a Kommo. Si el lead trae campaña de Meta, va después del punto. El % de la barra es la parte de ese estado.
        </p>
      </div>
      <div className="leads-status-chart">
        <ul className="leads-status-chart-legend">
          {model.series.map((s) => (
            <li key={s.key}>
              <span className="leads-status-swatch" style={{ background: s.color }} />
              <span title={s.label}>
                {s.label}
                {' · '}
                {fmt(s.total)}
                {' · '}
                {pct(s.total, grand)}
              </span>
            </li>
          ))}
        </ul>
        <div className="leads-status-chart-scroll" role="img" aria-label="Barras por estado y campaña">
          <div className="leads-status-chart-plot">
            {model.states.map((st) => (
              <div key={st.id} className="leads-status-group">
                <div className="leads-status-bars">
                  {model.series.map((serie) => {
                    const n = st.counts[serie.key] || 0;
                    const stateTotal = model.series.reduce((sum, s) => sum + (st.counts[s.key] || 0), 0);
                    const share = pct(n, stateTotal);
                    const h = n ? Math.max(4, Math.round((n / model.max) * 100)) : 0;
                    return (
                      <div
                        key={serie.key}
                        className="leads-status-bar"
                        style={{ height: h + '%', background: n ? serie.color : 'transparent' }}
                        title={
                          (st.name || st.id) +
                          ' · ' +
                          serie.label +
                          ': ' +
                          fmt(n) +
                          ' (' +
                          share +
                          ' de este estado)'
                        }
                      >
                        {n ? <span>{share}</span> : null}
                      </div>
                    );
                  })}
                </div>
                <p className="leads-status-xlabel" title={st.name || st.id}>
                  {st.name || st.id}
                </p>
              </div>
            ))}
          </div>
        </div>
        <div className="analytics-table-wrap" style={{ marginTop: 14 }}>
          <table className="analytics-table">
            <thead>
              <tr>
                <th>Cómo llegó</th>
                <th>En Meta</th>
                <th className="num">Leads</th>
                <th className="num">% del total</th>
                {model.states.map((st) => (
                  <th key={st.id} className="num">
                    {st.name || st.id}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {model.series.map((serie) => (
                <tr key={serie.key}>
                  <td>
                    <span className="leads-status-swatch" style={{ background: serie.color }} />{' '}
                    {serie.origin || serie.label}
                  </td>
                  <td>{serie.meta || '—'}</td>
                  <td className="num">{fmt(serie.total)}</td>
                  <td className="num">{pct(serie.total, grand)}</td>
                  {model.states.map((st) => {
                    const n = st.counts[serie.key] || 0;
                    const stateTotal = model.series.reduce((sum, s) => sum + (st.counts[s.key] || 0), 0);
                    return (
                      <td key={st.id} className="num" title={fmt(n) + ' leads'}>
                        {n ? pct(n, stateTotal) : '—'}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
}
